import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { kebabCase } from 'lodash';
import * as React from 'react';
import type { JSX } from 'react';

import { generateTestId } from '../../lib/test-helpers';
import { IconPlus, IconLoadingSpinner } from '../icons';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center',
    'rounded-[4px] text-base font-semibold whitespace-nowrap',
    'ring-offset-background transition-colors',
    'focus-visible:ring-2 focus-visible:outline-hidden',
    'focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'font-sans leading-5',
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'border-wtw-stratosphere-450 bg-wtw-stratosphere-450 border text-white',
          'hover:border-wtw-stratosphere-550 hover:bg-wtw-stratosphere-550',
          'disabled:bg-wtw-violetmist-200 disabled:border-wtw-violetmist-200 disabled:opacity-100',
          'active:border-wtw-stratosphere-450 active:bg-wtw-stratosphere-450',
          'focus-visible:ring-wtw-stratosphere-175',
        ),
        secondary: cn(
          'border-wtw-graymatter-200 text-wtw-graymatter-800 border bg-white',
          'disabled:border-wtw-violetmist-200 disabled:text-wtw-violetmist-200 disabled:bg-white disabled:opacity-100',
          'focus-visible:ring-wtw-violetmist-100 focus-visible:ring-offset-0',
        ),
        tertiary: cn('text-brand-400', 'hover:text-brand-300', 'disabled:text-steel-200', 'active:text-brand-400'),
        transparent: cn('text-steel-500', 'hover:text-steel-500', 'disabled:text-steel-200', 'active:text-steel-500'),
        // This one is not in the design system, but it's used in the app
        dangerous: cn(
          'border-wtw-coralreef-500 bg-wtw-coralreef-500 border text-white',
          'hover:border-wtw-coralreef-550 hover:bg-wtw-coralreef-550',
          'disabled:bg-wtw-violetmist-200 disabled:border-wtw-violetmist-200 disabled:opacity-100',
          'focus-visible:ring-wtw-stratosphere-175',
        ),
        inline: cn(
          'text-wtw-stratosphere-450 h-auto rounded-none px-0 py-0 underline',
          'hover:text-wtw-stratosphere-550',
          'disabled:text-wtw-violetmist-200 disabled:no-underline disabled:opacity-100',
          'focus-visible:ring-wtw-stratosphere-175',
        ),
        newfrontPink: cn(
          'border-rebrand-400 bg-rebrand-400 text-primary-foreground border',
          'hover:border-rebrand-300 hover:bg-rebrand-300',
          'disabled:border-steel-200 disabled:bg-steel-200',
          'active:border-rebrand-400 active:bg-rebrand-400',
        ),
      },
      size: {
        default: 'h-10 px-8 py-3',
        small: 'h-7 px-3 py-1 text-sm',
      },
      loading: {
        true: 'cursor-wait',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  'data-testid'?: string;
}

export interface ButtonDropzoneProps extends ButtonProps {
  icon?: JSX.Element;
}

export interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  'data-testid'?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, children, loading, variant, size, asChild = false, disabled, 'data-testid': propTestId, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const testId =
      propTestId ??
      (typeof children === 'string' ? generateTestId(kebabCase(children.toString()), 'button') : undefined);
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className, loading }))}
        ref={ref}
        disabled={disabled || loading}
        data-testid={testId}
        {...props}
      >
        {loading && <IconLoadingSpinner className="text-steel-400 mr-3" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

const ButtonDropzone = React.forwardRef<HTMLButtonElement, ButtonDropzoneProps>(
  ({ children, icon = <IconPlus />, className, ...props }, ref) => {
    return (
      <Button
        variant="tertiary"
        className={cn(
          'border-steel-200 text-brand-400 border border-dashed bg-white',
          'hover:border-brand-300',
          'disabled:border-steel-200 disabled:text-steel-200 h-10 py-6',
          className,
        )}
        ref={ref}
        {...props}
      >
        <div className="pr-2">{icon}</div>
        {children}
      </Button>
    );
  },
);
ButtonDropzone.displayName = 'ButtonDropzone';

const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ className, children, variant, size, 'data-testid': propTestId, ...props }, ref) => {
    const testId =
      propTestId ??
      (typeof children === 'string' ? generateTestId(kebabCase(children.toString()), 'button') : undefined);
    return (
      <a className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} data-testid={testId}>
        {children}
      </a>
    );
  },
);
ButtonLink.displayName = 'ButtonLink';

export { Button, ButtonDropzone, ButtonLink, buttonVariants };
